import { Injectable } from '@angular/core';
import { CollectionReference, DocumentData, Firestore, QueryDocumentSnapshot, QuerySnapshot, addDoc, collectionData, doc, docData, getDocs, limit, query, setDoc, updateDoc, where } from '@angular/fire/firestore';
import { collection } from '@firebase/firestore';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { SharedService } from '../../../../shared/shared.service';
import { BehaviorSubject, Observable, map, of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ProgramPageService {
	progressCollection: CollectionReference<DocumentData>;
	usersCollection: CollectionReference<DocumentData>;
	quotesCollection: CollectionReference<DocumentData>;
	progress$ = new BehaviorSubject<any>(0);
	organisationsCollection: CollectionReference<DocumentData>;

	constructor(
		private firestore: Firestore,
		private sharedService: SharedService,
		private fns: AngularFireFunctions
	) {
		this.progressCollection = collection(this.firestore, "progress");
		this.usersCollection = collection(this.firestore, "users");
		this.quotesCollection = collection(this.firestore, "inspirationalQuotes");
		this.organisationsCollection = collection(this.firestore, "organisations");
	}

	async generateIspringQuizToken(progressId: string, sectionIndex: number, unitIndex: number): Promise<string> {
		const generateToken = this.fns.httpsCallable('program-generateIspringQuizToken');
		const res: any = await this.sharedService.getObservableRes(generateToken({ progressId, sectionIndex, unitIndex }));
		return res?.token;
	}

	async saveDataConditional(progressId: string, userProgress: any, quizTry: boolean, videoPaused?: boolean, watchedDuration?: any) {
		userProgress.lastUpdatedAt = new Date();

		try {
			const docRef = doc(this.progressCollection, progressId);
			this.updateProgressStats(userProgress, quizTry, false, videoPaused, watchedDuration);
			await updateDoc(docRef, userProgress);
		} catch (err) {
			console.log('Unable to save data', err);
		}
	}

	updateProgressStats(programJson: any, quizTry: boolean, initialTry: boolean, videoPaused?: boolean, watchedDuration?: any) {
		let currDate = new Date();
		let currMonth = this.getFormattedMonth(currDate);
		let currDateFormatted = this.getFormattedDate(currDate) + "-" + currMonth + "-" + currDate.getFullYear();
		let monthAndYear = currMonth + "-" + currDate.getFullYear();

		if (initialTry) {
			programJson.totalLearningHrs = this.getTotalLearningHrs(programJson.sections);
			programJson.totalQuizzes = this.getTotalQuizzes(programJson.sections);
		}

		if (quizTry) {
			programJson.quizProgress = this.getQuizProgress(programJson.sections);
			programJson.quizAnswerProgress = this.getQuizAnswerProgress(programJson.sections);
			programJson.attemptedQuizzes = this.getAttemptedQuizzes(programJson.sections);
			programJson.earnedPts = this.getEarnedPts(programJson.sections);
			programJson.progressTracker.push(programJson.progress);
		} else {
			programJson.videoProgress = this.getVideoProgress(programJson.sections);
			programJson.learningDuration = this.getLearningDuration(programJson.sections);
			this.updateLearningHistory(programJson, monthAndYear, watchedDuration);

			if (videoPaused) {
				programJson.progressTracker.push(programJson.progress);
			}
		}

		const calculatedProgress = this.getOverallProgress(programJson.sections);
		programJson.progress = calculatedProgress;
		this.progress$.next(programJson.progress);

		if (calculatedProgress === 100) {
			programJson.completedAt = new Date();
		}
		this.updateDaysLogged(programJson, currDateFormatted);
		this.updateDaysLoggedHistory(programJson, currDateFormatted, monthAndYear);
		this.updateDaysInRow(programJson, currDate, currDateFormatted);
	}

	getOverallProgress(sections: any) {
		const [ overallProgress, totalOverallProgress ] = this.calculateOverallProgress(sections);
		if (!Number.isFinite(totalOverallProgress) || totalOverallProgress <= 0) {
			return 0;
		}

		const progress = Math.floor((overallProgress * 100) / totalOverallProgress);
		return Number.isFinite(progress) ? progress : 0;
	}

	calculateOverallProgress(sections: any) {
		let progress = 0;
		let totalProgress = 0;

		if (sections && sections.length >= 1) {
			for (let section of sections) {
				if (section.type === 'menu' || section.type === 'Menu') {
					let [ overallProgress, overallTotalProgress ] = this.calculateOverallProgress(section.menu);
					progress += overallProgress;
					totalProgress += overallTotalProgress;
				} else if (section.type === 'video' || section.type === 'Video') {
					for (let topic of section.metadata.topics) {
						const topicProgress = Number(topic.progress);
						progress += Number.isFinite(topicProgress) ? topicProgress : 0;
						totalProgress += 100;
					}
				} else if (section.type === 'quiz' || section.type === 'Quiz') {
					const sectionProgress = Number(section.progress);
					progress += Number.isFinite(sectionProgress) ? sectionProgress : 0;
					totalProgress += 100;
				} else if (section.type === 'download' || section.type === 'Download') {
					for (let document of section.metadata.documents) {
						const documentProgress = Number(document.progress);
						progress += Number.isFinite(documentProgress) ? documentProgress : 0;
						totalProgress += 100;
					}
				}
			}
		}

		return [ progress, totalProgress ];
	}

	getTotalLearningHrs(sections: any) {
		let learningHrs = 0;

		if (sections?.length >= 1) {
			for (let section of sections) {
				if (section.type === 'menu' || section.type === 'Menu') {
					learningHrs += this.getTotalLearningHrs(section.menu);
				} else if (section.type === 'video' || section.type === 'Video') {
					learningHrs += section.metadata.videoDuration;
				}
			}
		}

		return learningHrs;
	}

	getTotalQuizzes(sections: any) {
		let quizCount = 0;

		if (sections?.length >= 1) {
			for (let section of sections) {
				if (section.type === 'menu' || section.type === 'Menu') {
					quizCount += this.getTotalQuizzes(section.menu);
				} else if (section.type === 'quiz' || section.type === 'Quiz') {
					quizCount += 1;
				}
			}
		}

		return quizCount;
	}

	getQuizProgress(sections: any) {
		const [ quizProgress, totalQuizProgress ] = this.calculateQuizProgress(sections);
		return Math.floor((quizProgress * 100) / totalQuizProgress);
	}

	calculateQuizProgress(sections: any) {
		let progress = 0;
		let totalProgress = 0;

		if (sections && sections.length >= 1) {
			for (let section of sections) {
				if (section.type === 'menu' || section.type === 'Menu') {
					let [ quizProgress, quizTotalProgress ] = this.calculateQuizProgress(section.menu);
					progress += quizProgress;
					totalProgress += quizTotalProgress;
				} else if (section.type === 'quiz' || section.type === 'Quiz') {
					if (section.metadata.attempted) {
						progress += 1;
					}
					totalProgress += 1;
				}
			}
		}

		return [ progress, totalProgress ];
	}

	getQuizAnswerProgress(sections: any) {
		const [ quizAnswerProgress, totalQuizAnswerProgress ] = this.calculateQuizAnswerProgress(sections);
		// console.log(quizAnswerProgress);
		// console.log(totalQuizAnswerProgress);
		const progressPercentage = Math.floor(quizAnswerProgress / totalQuizAnswerProgress);

		if (isNaN(progressPercentage)) {
			return 0;
		} else {
			return progressPercentage;
		}
	}

	calculateQuizAnswerProgress(sections: any) {
		let progress = 0;
		let totalProgress = 0;

		if (sections && sections.length >= 1) {
			for (let section of sections) {
				if (section.type === 'menu' || section.type === 'Menu') {
					let [ quizAnswerProgress, quizAnswerTotalProgress ] = this.calculateQuizAnswerProgress(section.menu);
					progress += quizAnswerProgress;
					totalProgress += quizAnswerTotalProgress;
				} else if (section.type === 'quiz' || section.type === 'Quiz') {
					if (section.metadata.attempted) {
						// console.log(section.answerProgress);
						progress += section.answerProgress;
						totalProgress += 1;
					}
				}
			}
		}

		return [ progress, totalProgress ];
	}

	getVideoProgress(sections: any) {
		const [ videoProgress, totalVideoProgress ] = this.calculateVideoProgress(sections);
		return Math.floor((videoProgress * 100) / totalVideoProgress);
	}

	calculateVideoProgress(sections: any) {
		let progress = 0;
		let totalProgress = 0;

		if (sections && sections.length >= 1) {
			for (let section of sections) {
				if (section.type === 'menu' || section.type === 'Menu') {
					let [ videoProgress, videoTotalProgress ] = this.calculateVideoProgress(section.menu);
					progress += videoProgress;
					totalProgress += videoTotalProgress;
				} else if (section.type === 'video' || section.type === 'Video') {
					progress += section.progress;
					totalProgress += 100;
				}
			}
		}

		return [ progress, totalProgress ];
	}

	getSectionProgress(sectionNo: any, sections: any) {
		let progress = 0;
		let totalProgress = 0;

		if (sections && sections.length >= 1) {
			for (let section of sections) {
				if (section.type === 'menu' || section.type === 'Menu') {
					let [ menuprogress, menuTotalProgress ] = this.getSectionProgress(sectionNo, section.menu);
					progress += menuprogress;
					totalProgress += menuTotalProgress;
				} else if (section.sno === sectionNo) {
					progress += section.progress;
					totalProgress += 100;
				}
			}
		}

		return [ progress, totalProgress ];
	}

	getAttemptedQuizzes(sections: any) {
		let quizCount = 0;

		if (sections?.length >= 1) {
			for (let section of sections) {
				if (section.type === 'menu' || section.type === 'Menu') {
					quizCount += this.getAttemptedQuizzes(section.menu);
				} else if ((section.type === 'quiz' || section.type === 'Quiz') && section.metadata.attempted) {
					quizCount += 1;
				}
			}
		}

		return quizCount;
	}

	getEarnedPts(sections: any) {
		let earnedPts = 0;

		if (sections?.length >= 1) {
			for (let section of sections) {
				if (section.type === 'menu' || section.type === 'Menu') {
					earnedPts += this.getEarnedPts(section.menu);
				} else if ((section.type === 'quiz' || section.type === 'Quiz') && section.metadata.attempted) {
					earnedPts += section.earnedPts;
				}
			}
		}

		return earnedPts;
	}

	getLearningDuration(sections: any) {
		let learningDuration = 0;

		if (sections?.length >= 1) {
			for (let section of sections) {
				if (section.type === 'menu' || section.type === 'Menu') {
					learningDuration += this.getLearningDuration(section.menu);
				} else if (section.type === 'video' || section.type === 'Video') {
					learningDuration += section.metadata.learningDuration;
				}
			}
		}

		return learningDuration;
	}

	getFormattedMonth(currDate: Date) {
		return Number(currDate.getMonth() + 1) < 10 ? "0" + Number(currDate.getMonth() + 1) : String(currDate.getMonth() + 1);
	}

	getFormattedDate(currDate: Date) {
		let currentDate = currDate.getDate();
		return (currentDate < 10 ? '0' + currentDate : currentDate);
	}

	updateLearningHistory(programJson: any, monthAndYear: any, watchedDuration: any) {
		let learningHistory = programJson?.learningHistory;

		if (learningHistory) {
			if (learningHistory[monthAndYear]) {
				learningHistory[monthAndYear] += 2;
			} else {
				learningHistory[monthAndYear] = 2;
			}
		}
	}

	updateDaysLogged(programJson: any, formattedDate: String) {
		if (!programJson.daysLogged.includes(formattedDate)) {
			programJson.daysLogged.push(formattedDate);
		}
	}

	updateDaysLoggedHistory(programJson: any, currDateFormatted: String, monthAndYear: any) {
		let daysLoggedHistory = programJson?.daysLoggedHistory;

		if (daysLoggedHistory) {
			if (daysLoggedHistory[monthAndYear]) {
				if (!daysLoggedHistory[monthAndYear].includes(currDateFormatted)) {
					daysLoggedHistory[monthAndYear].push(currDateFormatted);
				}
			} else {
				daysLoggedHistory[monthAndYear] = [currDateFormatted];
			}
		}
	}

	updateDaysInRow(programJson: any, currDate: Date, currDateFormatted: String) {
		let daysInRow = programJson?.daysInRow;
		if (daysInRow.includes(currDateFormatted)) {
			return;
		}

		if (daysInRow.length === 0) {
			programJson.daysInRow = [currDateFormatted];
			return;
		}

		let lastDayInRow = daysInRow[daysInRow.length - 1];
		let [lastDayDate, lastDayMonth, lastDayYear] = lastDayInRow.split("-");
		let lastDateRow = new Date(lastDayYear, lastDayMonth, lastDayDate);

		if (this.sharedService.checkDateDifference(currDate, lastDateRow) <= 2) {
			daysInRow.push(currDateFormatted);
		} else {
			programJson.daysInRow = [currDateFormatted];
		}
	}

	async getIdByEmail(email: string) {
		try {
			const userQuery = query(this.usersCollection, where("email", "==", email));
			const userQuerySnap = await getDocs(userQuery);

			if (!userQuerySnap.empty) {
				return userQuerySnap.docs[0].id;
			}

			return "";
		} catch (err) {
			console.log('Unable to get id by email', err);
		}
	}

	setLaunchData(programJson: any) {
		let jsonCopy: any = {};

		for (let [key, value] of Object.entries(programJson)) {
			if (key != 'default') {
				jsonCopy[key] = value;
			}
		}
		programJson = jsonCopy;

		const newDate = new Date();
		programJson.progress = 0;
		programJson.attemptedQuizzes = 0;
		programJson.quizProgress = 0;
		programJson.quizAnswerProgress = 0;
		programJson.earnedPts = 0;
		programJson.attemptedQuizzes = 0;
		programJson.learningDuration = 0;
		programJson.videoProgress = 0;
		programJson.learningDuration = 0;
		programJson.progressTracker = [];
		programJson.daysLogged = [];
		programJson.learningHistory = {};
		programJson.daysLoggedHistory = {};
		programJson.daysInRow = [];
		programJson.startedAt = newDate;
		programJson.lastUpdatedAt = newDate;
		const currentMonthAndYear = this.sharedService.getCurrentMonthAndYear();
		programJson.dosesCompleted = {};
		programJson.dosesCompleted[currentMonthAndYear] = {};
		programJson.totalQuizzes = this.getTotalQuizzes(programJson.sections);
		return programJson;
	}

	prepareLaunchData(sections: any) {
		if (sections?.length >= 1) {
			for (let section of sections) {
				if (section.type === 'menu' || section.type === 'Menu') {
					section.completed = false;
					this.prepareLaunchData(section.menu);
				} else if (section.type === 'video' || section.type === 'Video') {
					section.progress = 0;
					section.metadata.learningDuration = 0;
					section.completed = false;

					let totalVideoDuration = 0;
					for (let topic of section?.metadata?.topics) {
						totalVideoDuration += Number(topic.videoDuration);
						topic.learningDuration = 0;
						topic.progress = 0;
					}
					section.metadata.videoDuration = totalVideoDuration;
				} else if (section.type === 'quiz' || section.type === 'Quiz') {
					section.metadata.totalCorrect = 0;
					section.earnedPts = 0;
					section.progress = 0;
					section.attempted = false;
					section.answerProgress = 0;
					section.completed = false;
					if (!section.quizType || section.quizType !== "ispring quiz") {
						const quizTopic = section.metadata?.topics?.find((topic: any) =>
							topic.interactions?.some((interaction: any) => interaction.question_group)
						);
						if (quizTopic) {
							const interaction = quizTopic.interactions.find((item: any) => item.question_group);
							section.metadata.questions = JSON.parse(interaction.question_group);
						}
					}
				} else if (section.type === 'download' || section.type === 'Download') {
					// console.log(section);
					section.progress = 0;
					for (let document of section.metadata.documents) {
						document.progress = 0;
					}
				}
			}
		}
	}

	async saveLaunchData(programJson: any) {
		var userId = localStorage.getItem("userId");

		if (userId) {
			programJson = this.setLaunchData(programJson);
			this.prepareLaunchData(programJson.sections);
			programJson.userId = userId;
			programJson.programId = programJson.id;
			const progressId = userId + "_" + programJson.id;
			programJson.totalLearningHrs = this.getTotalLearningHrs(programJson.sections);
			delete programJson.id;
			await setDoc(doc(this.firestore, 'progress', progressId), programJson);
		}
	}

	async isUserEnrolled(programId: string, userId: any) {
		try {
			const userQuery = query(this.progressCollection, where("userId", "==", userId), where('programId', '==', programId));
			const userQuerySnap = await getDocs(userQuery);
			if (!userQuerySnap.empty) {
				return true;
			}

			return false;
		} catch (err) {
			console.log('Unable to check if user is enrolled.', err);
		}
	}

	async saveDownloadProgress(progressId: any, userProgress: any) {
		try {
			const now = new Date();
			userProgress.lastUpdatedAt = now;
			userProgress.progress = this.getOverallProgress(userProgress.sections);
			if (userProgress.progress === 100) {
				userProgress.completedAt = now;
			}
			this.updateLearningDayTrackers(userProgress, now);
			this.progress$.next(userProgress.progress);
			const docRef = doc(this.progressCollection, progressId);
			await updateDoc(docRef, userProgress);
		} catch (err) {
			console.log('Unable to save download progress', err);
		}
	}

	async getProgressInProgram(programId: string, userId: any) {
		try {
			const userQuery = query(this.progressCollection, where("userId", "==", userId), where('programId', '==', programId));
			const userQuerySnap = await getDocs(userQuery);
			if (!userQuerySnap.empty) {
				const data: any = userQuerySnap.docs[0].data();
				return data.progress;
			}

			return 0;
		} catch (err) {
			console.log('Unable to check if user is enrolled.', err);
		}
	}

	/**
	 * This function will be used to save program progress when no other conditions are required to be met.
	 * Difference from saveDataConditional is that the latter requires either a quiz submission or video playback event
	 * publishing (basically it needs an event trigger other than function call), this one simply saves the progress when called.
	 */
	async saveData(progressId: string, userProgress: any) {
		try {
			const now = new Date();
			userProgress.lastUpdatedAt = now;
			this.updateLearningDayTrackers(userProgress, now);
			const docRef = doc(this.progressCollection, progressId);
			await updateDoc(docRef, userProgress);
		} catch (err) {
			console.log('Unable to save data', err);
		}
	}

	updateLearningDayTrackers(programJson: any, date: Date = new Date()) {
		if (!programJson || typeof programJson !== "object") {
			return;
		}

		if (!Array.isArray(programJson.daysLogged)) {
			programJson.daysLogged = [];
		}

		if (!programJson.daysLoggedHistory || typeof programJson.daysLoggedHistory !== "object") {
			programJson.daysLoggedHistory = {};
		}

		if (!Array.isArray(programJson.daysInRow)) {
			programJson.daysInRow = [];
		}

		const currMonth = this.getFormattedMonth(date);
		const currDateFormatted = this.getFormattedDate(date) + "-" + currMonth + "-" + date.getFullYear();
		const monthAndYear = currMonth + "-" + date.getFullYear();

		this.updateDaysLogged(programJson, currDateFormatted);
		this.updateDaysLoggedHistory(programJson, currDateFormatted, monthAndYear);
		this.updateDaysInRow(programJson, date, currDateFormatted);
	}

	async checkProgramExistenceAndProgressBarLock(): Promise<any | undefined> {
		const orgId: any = localStorage.getItem('orgId')
		const orgQuery = query(collection(this.firestore, 'organisations'));

		try {
		  const snapshot = await getDocs(orgQuery);
		  const matchingOrganization = snapshot.docs.find(doc => doc.id === orgId);
		  return matchingOrganization ? matchingOrganization.data() : undefined;
		} catch (error) {
		  console.error('Error fetching organizations:', error);
		  return undefined;
		}
	  }

	async getProgramData(programId: string) {
			const obs = docData(doc(this.firestore, `programs/${programId}`));
			const programData = await this.sharedService.getObservableRes(obs);
			return programData;
	}

	getProgressDetails(programId: string, userId: any) {
		try {
			const qr = query(
				this.progressCollection,
				where("userId", "==", userId),
				where("programId", "==", programId),
				limit(1)
			);

			return collectionData(qr, { idField: "id" }).pipe(
				map((docs) => (docs.length > 0 ? docs[0] : null))
			) as Observable<any>;
		} catch (error) {
			console.error("Error retrieving progress:", error);
			return new Observable<any>();
		}
	}

	// async checkProgramExistenceAndProgressBarLock(programId: string): Promise<boolean> {
	// 	const organizationsSnapshot = await getDocs(this.organisationsCollection);
	// 	let programExists = false;

	// 	organizationsSnapshot.forEach((orgDoc) => {
	// 	  const allowedPrograms: string[] = orgDoc.get('allowedPrograms');
	// 	  if (allowedPrograms && allowedPrograms.includes(programId)) {
	// 		programExists = true;
	// 		const progressBarLock = orgDoc.get('progressbarlock');
	// 		console.log('progressBarLock', progressBarLock)
	// 		if (typeof progressBarLock === 'boolean') {
	// 		  return progressBarLock;
	// 		}
	// 	  }
	// 	});

	// 	console.log('programExists', programExists)

	// 	return programExists;
	//   }

	// import { DocumentData } from 'firebase/firestore';



}
